import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  MoreVertical,
  Volume2,
  VolumeX,
  PictureInPicture,
  BookOpen,
  User,
  Settings as SettingsIcon,
  Sun,
  Moon,
  LogOut,
  Globe,
} from "lucide-react";
import type { AppMode } from "../hooks/useTranslator";

interface HeaderKebabMenuProps {
  appMode: AppMode;
  voiceEnabled: boolean;
  pipActive: boolean;
  theme: "light" | "dark";
  uiLang: "en" | "es";
  t: any; // Using the localized strings passed down
  onToggleVoice: () => void;
  onTogglePip: () => void;
  onOpenDocs: () => void;
  onOpenProfile: () => void;
  onOpenHistory: () => void;
  onOpenSettings: () => void;
  onToggleTheme: () => void;
  onToggleUiLang: () => void;
  onLogout: () => void;
}

export const HeaderKebabMenu: React.FC<HeaderKebabMenuProps> = ({
  appMode,
  voiceEnabled,
  pipActive,
  theme,
  uiLang,
  t,
  onToggleVoice,
  onTogglePip,
  onOpenDocs,
  onOpenProfile,
  onOpenHistory,
  onOpenSettings,
  onToggleTheme,
  onToggleUiLang,
  onLogout,
}) => {
  const [kebabOpen, setKebabOpen] = useState(false);
  const kebabRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const dropdownEl = document.getElementById("kebab-dropdown");
      if (
        kebabRef.current && !kebabRef.current.contains(target) &&
        !(dropdownEl && dropdownEl.contains(target))
      ) {
        setKebabOpen(false);
      }
    };
    if (kebabOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [kebabOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!kebabOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
    setKebabOpen((v) => !v);
  };

  const btnBase =
    "inline-flex items-center justify-start text-left gap-[6px] py-[6px] px-[14px] rounded-[6px] border text-[13px] font-medium transition-all duration-150 active:scale-95 cursor-pointer";
  const btnGhost = `${btnBase} border-[var(--border)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text)]`;

  return (
    <div className="relative inline-flex items-center" ref={kebabRef}>
      {/* Trigger */}
      <button
        ref={btnRef}
        onClick={handleToggle}
        title={t.moreOptions}
        aria-label={t.moreOptions}
        aria-expanded={kebabOpen}
        aria-haspopup="true"
        className={`inline-flex items-center justify-center py-[6px] px-[10px] rounded-[6px] border text-[16px] font-bold transition-all duration-150 active:scale-95 cursor-pointer ${
          kebabOpen
            ? "border-[var(--accent)]/40 bg-[var(--accent-muted)] text-[var(--accent)]"
            : "border-[var(--border)] bg-transparent text-[var(--text-muted)] hover:bg-white/10 hover:text-[var(--text)]"
        }`}
      >
        <MoreVertical size={16} />
      </button>

      {/* Dropdown — rendered via portal to escape header overflow:hidden */}
      {kebabOpen && createPortal(
        <div
          id="kebab-dropdown"
          className="fixed z-[999] min-w-[188px] flex flex-col gap-[6px] p-[10px] rounded-[8px] bg-[var(--surface)] border border-[var(--border)] shadow-[0_4px_16px_rgba(0,0,0,0.4)]"
          style={{
            top: dropdownPos.top,
            right: dropdownPos.right,
            animation: "modal-appear 0.18s cubic-bezier(0.16,1,0.3,1)",
          }}
        >
          {/* Voice */}
          {appMode === "traductor" && (
            <button
              onClick={() => {
                onToggleVoice();
                setKebabOpen(false);
              }}
              title="Activar/Desactivar lectura de voz"
              aria-label={voiceEnabled ? "Desactivar lectura de voz" : "Activar lectura de voz"}
              className={`w-full justify-start ${btnBase} ${
                voiceEnabled
                  ? "bg-[rgba(48,209,88,0.22)] border-[rgba(48,209,88,0.4)] text-[var(--success)] shadow-[0_0_8px_rgba(48,209,88,0.2)]"
                  : "bg-[rgba(48,209,88,0.1)] border-[rgba(48,209,88,0.25)] text-[var(--success)] hover:bg-[rgba(48,209,88,0.18)]"
              }`}
            >
              {voiceEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
              <span>{voiceEnabled ? t.voiceOn : t.voiceOff}</span>
            </button>
          )}

          {/* PiP */}
          {appMode === "traductor" && (
            <button
              onClick={() => {
                onTogglePip();
                setKebabOpen(false);
              }}
              title="Ventana PiP flotante"
              aria-label="Alternar ventana PiP flotante"
              className={`w-full justify-start ${btnBase} ${
                pipActive
                  ? "bg-[var(--accent-muted)] border-[var(--accent)]/40 text-[var(--accent)] shadow-[0_0_8px_rgba(0,122,255,0.2)]"
                  : btnGhost
              }`}
            >
              <PictureInPicture size={14} />
              <span>{t.pip}</span>
            </button>
          )}

          {/* Docs */}
          <button
            onClick={() => {
              onOpenDocs();
              setKebabOpen(false);
            }}
            title="Documentación"
            className={`w-full justify-start ${btnGhost}`}
          >
            <BookOpen size={14} />
            <span>{t.docs}</span>
          </button>

          {/* Profile option */}
          <button
            onClick={() => {
              onOpenProfile();
              setKebabOpen(false);
            }}
            title={t.profile}
            className={`w-full justify-start ${btnGhost}`}
          >
            <User size={14} />
            <span>{t.profile}</span>
          </button>


          {/* Settings option */}
          <button
            onClick={() => {
              onOpenSettings();
              setKebabOpen(false);
            }}
            title={uiLang === "en" ? "Settings" : "Configuración"}
            className={`w-full justify-start ${btnGhost}`}
          >
            <SettingsIcon size={14} />
            <span>{uiLang === "en" ? "Settings" : "Configuración"}</span>
          </button>

          {/* Separator */}
          <div className="border-t border-[var(--border)] my-1" />

          {/* Theme */}
          <button
            onClick={() => {
              onToggleTheme();
              setKebabOpen(false);
            }}
            title="Cambiar tema visual"
            aria-label={theme === "dark" ? "Cambiar a tema claro" : "Cambiar a tema oscuro"}
            className={`w-full justify-start ${btnGhost}`}
          >
            {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
            <span>{theme === "dark" ? t.themeLight : t.themeDark}</span>
          </button>

          {/* UI Language toggle */}
          <button
            onClick={() => {
              onToggleUiLang();
            }}
            title="Cambiar idioma de la interfaz"
            aria-label="Alternar idioma de la interfaz"
            className={`w-full justify-start ${btnBase} border-[var(--accent)]/30 bg-[var(--accent-muted)] text-[var(--accent)] hover:bg-[var(--accent-muted)] font-mono tracking-wide`}
          >
            <Globe size={14} />
            <span>{t.uiLangLabel}</span>
          </button>

          {/* Logout */}
          <button
            onClick={() => {
              onLogout();
              setKebabOpen(false);
            }}
            title={t.logout}
            aria-label="Cerrar sesión"
            className={`w-full justify-start ${btnBase} border-[var(--border)] bg-[var(--surface)] text-rose-400/70 hover:bg-[var(--surface-hover)] hover:text-rose-400`}
          >
            <LogOut size={14} />
            <span>{t.logout}</span>
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};
